<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    DrawerTrigger
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast/use-toast';
import { trackEvent } from '@/utils/analytics';
import { Loader, LocateFixedIcon, XIcon } from 'lucide-vue-next';
import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps<{
    currentPostalCode?: string;
}>();

// @ts-ignore
const emit = defineEmits<{
    (e: 'update:location', payload: { postalCode?: string; lat?: number; lng?: number }): void;
}>();

const router = useRouter();
const route = useRoute();
const { toast } = useToast();

const open = ref(false);
const postalCode = ref(props.currentPostalCode || '');
const isGettingLocation = ref(false);
const postalCodeError = ref('');

// validate canadian postal code (format: V#X #X#)
const isValidPostalCode = computed(() => {
    const cleaned = postalCode.value.replace(/\s/g, '').toUpperCase();
    return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(cleaned);
});

// validate BC postal code (starts with V)
const isBCPostalCode = computed(() => {
    const cleaned = postalCode.value.replace(/\s/g, '').toUpperCase();
    return cleaned.startsWith('V');
});

// format postal code
watch(postalCode, (value) => {
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    if (cleaned.length >= 3 && cleaned.length <= 6) {
        if (cleaned.length > 3) {
            postalCode.value = cleaned.slice(0, 3) + ' ' + cleaned.slice(3);
        } else {
            postalCode.value = cleaned;
        }
    }
    postalCodeError.value = '';
});

function handlePostalCodeSubmit() {
    const cleaned = postalCode.value.replace(/\s/g, '');
    
    if (!isValidPostalCode.value) {
        postalCodeError.value = 'Please enter a valid postal code';
        return;
    }
    
    if (!isBCPostalCode.value) {
        postalCodeError.value = 'Please enter a BC postal code';
        return;
    }

    trackEvent('location_drawer_postal_code_submitted', {});

    // update query params with postal code only
    router.replace({ 
        query: { 
            ...route.query, 
            postalCode: cleaned,
            lat: undefined,
            lng: undefined
        } 
    });
    
    shouldCleanupHistory = false;
    open.value = false;
}

async function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
        toast({
            title: 'Geolocation not supported',
            description: 'Your browser does not support geolocation',
            variant: 'destructive'
        });
        return;
    }

    isGettingLocation.value = true;

    navigator.geolocation.getCurrentPosition(
        (position) => {
            // update query params with lat/lng
            const currentPostal = route.query.postalCode || postalCode.value.replace(/\s/g, '');
            router.push({ 
                query: { 
                    ...route.query, 
                    postalCode: currentPostal || undefined,
                    lat: position.coords.latitude.toFixed(6),
                    lng: position.coords.longitude.toFixed(6)
                } 
            });

            trackEvent('location_drawer_current_location_used', {});

            shouldCleanupHistory = false;
            open.value = false;
            isGettingLocation.value = false;
        },
        (error) => {
            isGettingLocation.value = false;
            let message = 'Could not get your location';
            
            switch(error.code) {
                case error.PERMISSION_DENIED:
                    message = 'Location permission denied. Please enable location access in your browser settings.';
                    break;
                case error.POSITION_UNAVAILABLE:
                    message = 'Location information unavailable';
                    break;
                case error.TIMEOUT:
                    message = 'Location request timed out';
                    break;
            }
            
            toast({
                title: 'Location Error',
                description: message,
                variant: 'destructive'
            });
        },
        {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        }
    );
}

// handle browser back button
function handlePopstate() {
    if (open.value) {
        shouldCleanupHistory = false;
        open.value = false;
    }
}

// handle escape key
function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && open.value) {
        e.preventDefault();
        open.value = false;
    }
}

// track if we should clean up history
let shouldCleanupHistory = false;

// watch open state to manage history and event listeners
watch(open, (isOpen) => {
    if (isOpen) {
        history.pushState({ drawerOpen: true }, '', window.location.href);
        shouldCleanupHistory = true;
        window.addEventListener('popstate', handlePopstate);
        document.addEventListener('keydown', handleKeydown);
    } else {
        window.removeEventListener('popstate', handlePopstate);
        document.removeEventListener('keydown', handleKeydown);
        
        if (shouldCleanupHistory && history.state?.drawerOpen === true) {
            history.back();
        }
        shouldCleanupHistory = false;
    }
});

// cleanup on component unmount
onUnmounted(() => {
    window.removeEventListener('popstate', handlePopstate);
    document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
    <Drawer v-model:open="open">
        <DrawerTrigger as-child>
            <slot />
        </DrawerTrigger>
        <DrawerOverlay class="backdrop-blur-sm" />
        <DrawerContent class="p-4 pt-0 max-h flex max-h-[85vh] flex-col rounded-t-3xl w-full max-w-md mx-auto">
            <DrawerHeader class="px-0 py-2 flex justify-between items-center mb-0">
                <div class="text-2xl leading-relaxed font-semibold text-gray-900">Change Search Location</div>
                <DrawerClose>
                    <span class="sr-only">Close</span>
                    <XIcon class="size-10 translate-x-2 p-2 text-gray-500" />
                </DrawerClose>
            </DrawerHeader>

            <div class="flex flex-col gap-y-4 pb-20">
                <div class="text-gray-400">
                    Update your postal code to see nearby pharmacies first.
                </div>

                <div>
                    <label class="text-sm font-medium text-gray-600">Enter Postal Code</label>
                    <div class="mt-2 space-y-2">
                        <div class="flex flex-row gap-2">
                            <Input v-model="postalCode" placeholder="e.g. V3S 6K7" maxlength="7"
                                class="placeholder:normal-case uppercase text-base font-semibold text-gray-900 tabular-nums"
                                @keyup.enter="handlePostalCodeSubmit" />
                            <Button @click="handlePostalCodeSubmit"
                                class="shrink-0 h-12 px-6 text-base font-semibold">
                                Done
                            </Button>
                        </div>
                        <div v-if="postalCodeError" class="text-sm text-red-500">
                            {{ postalCodeError }}
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-3">
                    <div class="flex-1 h-px bg-gray-200"></div>
                    <span class="text-gray-500 font-medium text-sm">OR</span>
                    <div class="flex-1 h-px bg-gray-200"></div>
                </div>

                <Button variant="outline" @click="handleUseCurrentLocation" :disabled="isGettingLocation"
                    class="w-full h-13 justify-center text-gray-900 font-semibold text-base">
                    <LocateFixedIcon v-if="!isGettingLocation" class="size-6 mr-2" />
                    <Loader v-else class="size-5 mr-2 animate-spin" />
                    Use My Current Location
                </Button>
            </div>
        </DrawerContent>
    </Drawer>
</template>